import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Users, Plus, Edit2, Archive, X, Save, RefreshCw, Upload, Image,
  Calendar, DollarSign, MapPin, Clock, GraduationCap, Eye, UserPlus, Mail,
  RotateCcw, History, AlertTriangle, CalendarPlus, Rocket, Share2
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import { Bold, Italic, Link as LinkIcon } from 'lucide-react';
import { EmailParticipantsModal } from '../components/EmailParticipantsModal';
import { NextTermModal } from '../components/NextTermModal';
import { ShareRosterModal } from '../components/ShareRosterModal';
import { PrivateLessonSettings } from '../components/PrivateLessonSettings';
import { isPrivateLesson } from '../utils/privateLesson';
import { isProgramPublic } from '../utils/programVisibility';
import { ClassArchivePanel, ArchiveClassDialog } from '../components/ClassArchivePanel';

const API_URL = "";

// Rich Text Editor Component
const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-cafa-blue underline' },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: 'focus:outline-none' },
      handleDOMEvents: {
        focus: (view, event) => { event.preventDefault(); return false; },
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus({ scrollIntoView: false }).extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus({ scrollIntoView: false }).extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-stone-300 rounded-md overflow-hidden">
      <div className="flex items-center gap-1 p-2 bg-stone-100 border-b border-stone-300">
        <button type="button" onClick={() => editor.chain().focus({ scrollIntoView: false }).toggleBold().run()}
          className={`p-2 rounded hover:bg-stone-200 ${editor.isActive('bold') ? 'bg-stone-300' : ''}`}>
          <Bold size={16} />
        </button>
        <button type="button" onClick={() => editor.chain().focus({ scrollIntoView: false }).toggleItalic().run()}
          className={`p-2 rounded hover:bg-stone-200 ${editor.isActive('italic') ? 'bg-stone-300' : ''}`}>
          <Italic size={16} />
        </button>
        <div className="w-px h-6 bg-stone-300 mx-1" />
        <button type="button" onClick={setLink}
          className={`p-2 rounded hover:bg-stone-200 ${editor.isActive('link') ? 'bg-stone-300' : ''}`}>
          <LinkIcon size={16} />
        </button>
      </div>
      <EditorContent editor={editor} className="prose prose-stone prose-sm max-w-none p-3 min-h-[150px] focus:outline-none [&_.ProseMirror]:min-h-[130px] [&_.ProseMirror]:outline-none" />
    </div>
  );
};

export const AdminKidsClasses = () => {
  const { user, token } = useAuth();
  const [kidsClasses, setKidsClasses] = useState([]);
  const [archivedClasses, setArchivedClasses] = useState([]);
  const [archiveView, setArchiveView] = useState(false);
  const [archivingClass, setArchivingClass] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [enrollmentCounts, setEnrollmentCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingClass, setEditingClass] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(null);
  const [shareRoster, setShareRoster] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [rosterTerm, setRosterTerm] = useState('current');     // current | all | <label>
  const [termHistory, setTermHistory] = useState([]);
  const [showAddStudentModal, setShowAddStudentModal] = useState(null);
  const [refundModal, setRefundModal] = useState(null);        // { student, classId }
  const [refundForm, setRefundForm] = useState({ mode: 'cancel_only', reason: '', send_email: true });
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [emailClass, setEmailClass] = useState(null);
  const [nextTerm, setNextTerm] = useState(null);            // { singleProgram } | { }
  const [publishing, setPublishing] = useState(null);        // class id being published
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);

  const emptyClass = {
    title: '',
    instructor: '',
    instructorEmail: '',
    instructorImage: '',
    classImage: '',
    images: [],
    schedule: '',
    duration: '12 weeks',
    time: '4:00 PM - 5:30 PM',
    description: '',
    level: 'Ages 5-7',
    ageRange: '5-7',
    price: 350,
    memberPrice: 300,
    materials: '',
    location: '115 Melrose Place, San Antonio, TX',
    maxStudents: 12,
    category: 'Youth',
    class_type: 'enrollment_managed',
    scheduling_mode: 'term',
    startDate: '',
    endDate: '',
    daysOfWeek: 'Wednesday'
  };

  const emptyStudent = {
    student_name: '',
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    amount_paid: 0,
    payment_method: 'paypal',
    notes: ''
  };

  const [formData, setFormData] = useState(emptyClass);
  const [studentFormData, setStudentFormData] = useState(emptyStudent);

  useEffect(() => {
    if (token && isAdmin) {
      fetchData();
    }
  }, [token, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch programs and filter for Youth category
      const [programsRes, enrollmentsRes] = await Promise.all([
        fetch(`${API_URL}/api/content/admin/programs?state=all&category=Youth`, {
          headers: { Authorization: `Bearer ${token}` }, cache: 'no-store'
        }),
        fetch(`${API_URL}/api/content/enrollments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (programsRes.ok) {
        const programs = await programsRes.json();
        const youthPrograms = programs
          .filter(p => p.category === 'Youth' && p.status !== 'archived')
          .sort((a, b) => {
            // Drafts (next term being prepared) float to the top
            const da = (a.status || 'active') === 'draft' ? 0 : 1;
            const dbb = (b.status || 'active') === 'draft' ? 0 : 1;
            return da !== dbb ? da - dbb : a.id - b.id;
          });
        setKidsClasses(youthPrograms);
        setArchivedClasses(programs.filter(p => p.category === 'Youth' && p.status === 'archived'));
      }

      if (enrollmentsRes.ok) {
        const enrollments = await enrollmentsRes.json();
        const counts = {};
        enrollments.forEach(e => {
          if (e.id.startsWith('program_')) {
            const programId = String(e.id.replace('program_', ''));
            counts[programId] = e.count;
          }
        });
        setEnrollmentCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load kids classes');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.title || !formData.instructor) {
      toast.error('Please fill in required fields: Title and Instructor');
      return;
    }

    setSaving(true);
    try {
      // Add category as Youth
      const classData = { ...formData, category: 'Youth' };

      const response = await fetch(`${API_URL}/api/content/programs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(classData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create class');
      }

      toast.success('Kids class created successfully');
      setShowAddModal(false);
      setFormData(emptyClass);
      fetchData();
    } catch (error) {
      console.error('Error creating class:', error);
      toast.error(error.message || 'Failed to create class');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.instructor) {
      toast.error('Please fill in required fields: Title and Instructor');
      return;
    }

    setSaving(true);
    try {
      const classData = { ...formData, category: 'Youth' };
      delete classData.id;

      const response = await fetch(`${API_URL}/api/content/programs/${editingClass.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(classData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update class');
      }

      toast.success('Kids class updated successfully');
      setEditingClass(null);
      setFormData(emptyClass);
      fetchData();
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error(error.message || 'Failed to update class');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (program) => {
    if (restoringId || !window.confirm('Restore "' + program.title + '" as a draft? It will remain hidden until reviewed and published.')) return;
    setRestoringId(program.id);
    try {
      const response = await fetch('/api/content/programs/' + program.id + '/restore', { method: 'POST', headers: { Authorization: 'Bearer ' + token } });
      const body = await response.json();
      if (!response.ok) throw new Error(body.detail || 'Could not restore class');
      toast.success('Class restored as a draft. Review its dates and details before publishing.');
      setArchiveView(false); fetchData();
    } catch (error) { toast.error(error.message || 'Could not restore class'); }
    finally { setRestoringId(null); }
  };

  const handleEdit = (kidsClass) => {
    setEditingClass(kidsClass);
    setFormData({
      ...emptyClass,
      ...kidsClass
    });
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        if (field === 'images') {
          setFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), data.url]
          }));
        } else {
          setFormData(prev => ({ ...prev, [field]: data.url }));
        }
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (err) {
      toast.error('Error uploading image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const fetchAttendees = async (classId, term = rosterTerm) => {
    try {
      const url = `${API_URL}/api/content/programs/${classId}/enrollment?term=${encodeURIComponent(term)}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAttendees(data.registrations || data.students || []);
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
    }
  };

  const fetchTermHistory = async (classId) => {
    try {
      const res = await fetch(`${API_URL}/api/content/programs/${classId}/term-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTermHistory(data.terms || []);
      }
    } catch (e) {
      console.error('Error fetching term history:', e);
    }
  };

  const handleViewAttendees = async (kidsClass) => {
    setShowAttendeesModal(kidsClass);
    const term = kidsClass.status === 'archived' ? 'all' : 'current';
    setRosterTerm(term);
    setAttendees([]);
    await fetchAttendees(kidsClass.id, term);
    fetchTermHistory(kidsClass.id);
  };

  const handleAddStudent = async () => {
    if (!studentFormData.student_name || !studentFormData.parent_email) {
      toast.error('Please fill in student name and parent email');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/content/programs/${showAddStudentModal.id}/enrollment/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_name: studentFormData.student_name,
          student_email: studentFormData.parent_email,
          parent_name: studentFormData.parent_name,
          parent_email: studentFormData.parent_email,
          parent_phone: studentFormData.parent_phone,
          amount_paid: studentFormData.amount_paid,
          notes: `Parent: ${studentFormData.parent_name}, Phone: ${studentFormData.parent_phone}, Payment: ${studentFormData.payment_method}. ${studentFormData.notes}`
        })
      });

      if (response.ok) {
        toast.success('Student enrolled successfully');
        setShowAddStudentModal(null);
        setStudentFormData(emptyStudent);
        fetchData();
        if (showAttendeesModal) {
          fetchAttendees(showAttendeesModal.id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to enroll student');
      }
    } catch (error) {
      toast.error('Error enrolling student');
    } finally {
      setSaving(false);
    }
  };

  const openRefundModal = (classId, student) => {
    const hasPaypal = !!(student.paypal_capture_id || (student.payment_id && (student.amount_paid || 0) > 0));
    setRefundModal({ classId, student });
    setRefundForm({
      mode: hasPaypal ? 'paypal_auto' : ((student.amount_paid || 0) > 0 ? 'manual' : 'cancel_only'),
      reason: '',
      send_email: true,
    });
  };

  const handleRefundSubmit = async () => {
    if (!refundModal) return;
    setRefundSubmitting(true);
    try {
      const { classId, student } = refundModal;
      const res = await fetch(
        `${API_URL}/api/content/programs/${classId}/enrollment/${student.id}/refund`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(refundForm),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.success) {
        toast.success(
          refundForm.mode === 'paypal_auto'
            ? `Refunded $${body.amount_refunded} via PayPal`
            : refundForm.mode === 'manual'
              ? 'Marked as refunded (manual). Refund will be issued offline.'
              : 'Removed from roster (no refund needed)'
        );
        setRefundModal(null);
        fetchAttendees(classId, rosterTerm);
        fetchData();
      } else {
        toast.error(body.detail || body.message || 'Refund failed');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setRefundSubmitting(false);
    }
  };

  const handlePublishClass = async (kidsClass) => {
    setPublishing(kidsClass.id);
    try {
      const res = await fetch(`${API_URL}/api/content/programs/${kidsClass.id}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`"${kidsClass.title}" is now live and open for registration`);
        fetchData();
      } else {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Failed to publish class');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setPublishing(null);
    }
  };

  // Form Component
  const classFormContent = (isEdit = false) => (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Class Title <span className="text-red-500">*</span></Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="mt-1"
              placeholder="e.g., Coppini Kids - Grades K-2"
            />
          </div>
          <div>
            <Label>Instructor <span className="text-red-500">*</span></Label>
            <Input
              value={formData.instructor}
              onChange={(e) => setFormData(prev => ({ ...prev, instructor: e.target.value }))}
              className="mt-1"
              placeholder="Instructor name"
            />
          </div>
          <div>
            <Label>Instructor Email</Label>
            <Input
              type="email"
              value={formData.instructorEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, instructorEmail: e.target.value }))}
              className="mt-1"
              placeholder="instructor@email.com"
            />
          </div>
          <div>
            <Label>Age Group / Level</Label>
            <select
              value={formData.level}
              onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
            >
              <option value="Ages 5-7">Ages 5-7 (Grades K-2)</option>
              <option value="Ages 8-10">Ages 8-10 (Grades 3-5)</option>
              <option value="Ages 11-13">Ages 11-13 (Grades 6-8)</option>
              <option value="Ages 14-18">Ages 14-18 (High School)</option>
              <option value="All Ages">All Ages (5-18)</option>
            </select>
          </div>
          <div>
            <Label>Class Type</Label>
            <select
              value={formData.class_type}
              onChange={(e) => setFormData(prev => ({ ...prev, class_type: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
            >
              <option value="enrollment_managed">Enrollment Managed (Online Registration)</option>
              <option value="informational">Informational Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3">Description</h3>
        <RichTextEditor
          value={formData.description}
          onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
        />
      </div>

      {/* Class Format — term class vs private lesson */}
      <PrivateLessonSettings
        value={formData}
        onPatch={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
        idPrefix={isEdit ? 'kids_edit' : 'kids_add'}
      />

      {/* Schedule */}
      {!isPrivateLesson(formData) && (
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <Calendar size={18} /> Schedule
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Day(s) of Week</Label>
            <select
              value={formData.daysOfWeek}
              onChange={(e) => setFormData(prev => ({ ...prev, daysOfWeek: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
            >
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
            </select>
          </div>
          <div>
            <Label>Time</Label>
            <Input
              value={formData.time}
              onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
              className="mt-1"
              placeholder="e.g., 4:00 PM - 5:30 PM"
            />
          </div>
          <div>
            <Label>Duration</Label>
            <Input
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
              className="mt-1"
              placeholder="e.g., 12 weeks"
            />
          </div>
          <div>
            <Label>Schedule Text</Label>
            <Input
              value={formData.schedule}
              onChange={(e) => setFormData(prev => ({ ...prev, schedule: e.target.value }))}
              className="mt-1"
              placeholder="e.g., Wednesdays, Jan 15 - Apr 2"
            />
          </div>
          <div>
            <Label>Start Date</Label>
            <Input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label>End Date</Label>
            <Input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className="mt-1"
            />
          </div>
        </div>
      </div>
      )}

      {/* Pricing */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <DollarSign size={18} /> Pricing
        </h3>
        {isPrivateLesson(formData) ? (
          <p className="text-sm text-stone-600">
            Private lesson pricing is set per session length in the <strong>Class Format</strong> section above.
          </p>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Regular Price ($)</Label>
            <Input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Member Price ($)</Label>
            <Input
              type="number"
              value={formData.memberPrice}
              onChange={(e) => setFormData(prev => ({ ...prev, memberPrice: parseFloat(e.target.value) || 0 }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Max Students</Label>
            <Input
              type="number"
              min="1"
              max="30"
              value={formData.maxStudents}
              onChange={(e) => setFormData(prev => ({ ...prev, maxStudents: parseInt(e.target.value) || 12 }))}
              className="mt-1"
            />
          </div>
        </div>
        )}
      </div>

      {/* Instructor Photo */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <Users size={18} /> Instructor Photo
        </h3>
        <div className="flex items-start gap-4">
          {formData.instructorImage && (
            <div className="relative">
              <img
                src={formData.instructorImage.startsWith('/api') ? `${API_URL}${formData.instructorImage}` : formData.instructorImage}
                alt="Instructor"
                className="w-32 h-32 rounded-lg object-cover border border-stone-200"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, instructorImage: '' }))}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <div className="flex-1 space-y-3">
            <Input
              value={formData.instructorImage || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, instructorImage: e.target.value }))}
              placeholder="Enter instructor photo URL"
            />
            <p className="text-xs text-stone-500">Or upload an image using the Class Images section below</p>
          </div>
        </div>
      </div>

      {/* Class Images */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <Image size={18} /> Class Images
        </h3>
        <p className="text-sm text-stone-500 mb-3">Add photos showing the class in action or student artwork</p>

        <div className="flex gap-4 mb-4 flex-wrap">
          {(formData.images || []).map((img, index) => (
            <div key={index} className="relative">
              <img
                src={img.startsWith('/api') ? `${API_URL}${img}` : img}
                alt={`Class image ${index + 1}`}
                className="w-24 h-24 rounded-lg object-cover border border-stone-200"
              />
              <button
                type="button"
                onClick={() => {
                  const newImages = [...(formData.images || [])];
                  newImages.splice(index, 1);
                  setFormData(prev => ({ ...prev, images: newImages }));
                }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {(!formData.images || formData.images.length < 4) && (
            <div
              className="w-24 h-24 rounded-lg bg-stone-100 flex items-center justify-center border border-dashed border-stone-300 cursor-pointer hover:bg-stone-200"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? <RefreshCw size={24} className="text-stone-400 animate-spin" /> : <Upload size={24} className="text-stone-400" />}
            </div>
          )}
        </div>

        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={(e) => handleImageUpload(e, 'images')}
        />

        {(!formData.images || formData.images.length < 4) && (
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? <><RefreshCw size={16} className="mr-2 animate-spin" /> Uploading...</> : <><Upload size={16} className="mr-2" /> Upload Image</>}
            </Button>
          </div>
        )}
      </div>

      {/* Location & Materials */}
      <div>
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <MapPin size={18} /> Location & Materials
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label>Location</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Materials (what students should bring)</Label>
            <Textarea
              value={formData.materials}
              onChange={(e) => setFormData(prev => ({ ...prev, materials: e.target.value }))}
              className="mt-1"
              rows={2}
              placeholder="All materials provided, or list what students need to bring..."
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-stone-600">You don&apos;t have permission to access this page.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-kids-classes">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Kids Classes</h1>
            <p className="text-stone-600">Manage Coppini Kids youth art programs</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setNextTerm({ singleProgram: null })}
              className="border-cafa-red text-cafa-red hover:bg-cafa-red-50"
              data-testid="setup-next-term-btn"
              title="Copy existing classes forward into a new term as drafts"
            >
              <CalendarPlus size={18} className="mr-2" /> Set Up Next Term
            </Button>
            <Button onClick={() => { setShowAddModal(true); setFormData(emptyClass); }} className="bg-cafa-red hover:bg-cafa-red-900">
              <Plus size={18} className="mr-2" /> Add Kids Class
            </Button>
          </div>
        </div>

        <nav className="flex flex-wrap gap-2" aria-label="Class views">
          <Button variant={archiveView ? 'outline' : 'default'} aria-pressed={!archiveView} onClick={() => setArchiveView(false)}>Current Classes ({kidsClasses.length})</Button>
          <Button variant={archiveView ? 'default' : 'outline'} aria-pressed={archiveView} onClick={() => setArchiveView(true)} data-testid="class-archive-tab"><Archive size={16} className="mr-2" />Class Archive ({archivedClasses.length})</Button>
        </nav>
        {archiveView && <ClassArchivePanel programs={archivedClasses} token={token} onViewRoster={handleViewAttendees}
          onCopy={program => setNextTerm({ singleProgram: program })} onRestore={handleRestore} restoringId={restoringId} />}
        {!archiveView && <>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-stone-600">Total Classes</p>
              <p className="text-2xl font-bold text-stone-900">{kidsClasses.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-stone-600">Total Enrolled</p>
              <p className="text-2xl font-bold text-cafa-red">
                {kidsClasses.reduce((sum, c) => sum + (enrollmentCounts[String(c.id)] || 0), 0)}
              </p>
              <p className="text-[10px] text-stone-400 mt-0.5">Active enrolments across listed classes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-stone-600">Online Registration</p>
              <p className="text-2xl font-bold text-green-600">
                {kidsClasses.filter(c => c.class_type === 'enrollment_managed').length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-stone-600">Available Spots</p>
              <p className="text-2xl font-bold text-blue-600">
                {kidsClasses.reduce((total, c) => total + ((c.maxStudents || 12) - (enrollmentCounts[String(c.id)] || 0)), 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Classes List */}
        {loading ? (
          <div className="text-center py-12"><RefreshCw className="animate-spin mx-auto text-stone-400" size={32} /></div>
        ) : kidsClasses.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <GraduationCap className="mx-auto text-stone-300 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-stone-700 mb-2">No Kids Classes Yet</h3>
              <p className="text-stone-500 mb-4">Create your first kids class to get started</p>
              <Button onClick={() => setShowAddModal(true)} className="bg-cafa-red hover:bg-cafa-red-900">
                <Plus size={18} className="mr-2" /> Add Kids Class
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {kidsClasses.map((kidsClass) => {
              const isDraft = (kidsClass.status || 'active') === 'draft';
              const isPaused = (kidsClass.status || 'active') === 'paused';
              const missingDates = !kidsClass.startDate;
              const isPrivate = isPrivateLesson(kidsClass);
              return (
              <Card key={kidsClass.id} className={`overflow-hidden ${isDraft ? 'border-2 border-dashed border-amber-400' : ''}`} data-testid={`kids-class-card-${kidsClass.id}`}>
                {/* Class Image */}
                {(kidsClass.instructorImage || (kidsClass.images && kidsClass.images[0])) && (
                  <div className="h-40 overflow-hidden bg-stone-100">
                    <img
                      src={kidsClass.images?.[0] || kidsClass.instructorImage}
                      alt={kidsClass.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{kidsClass.level}</span>
                      {kidsClass.class_type === 'enrollment_managed' && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Online Registration</span>
                      )}
                      {isPrivate && (
                        <span className="text-xs bg-cafa-red-100 text-cafa-red-900 border border-cafa-red/30 px-2 py-1 rounded font-medium"
                          title="Private lesson — booked by appointment, no fixed day or time"
                          data-testid={`private-badge-${kidsClass.id}`}>
                          Private Lesson
                        </span>
                      )}
                      {kidsClass.term_of && (
                        <span className="text-xs bg-stone-800 text-white px-2 py-1 rounded" data-testid={`term-badge-${kidsClass.id}`}>
                          {kidsClass.term_of}
                        </span>
                      )}
                      {isDraft && (
                        <span className="text-xs bg-amber-100 text-amber-900 border border-amber-300 px-2 py-1 rounded font-medium"
                          title="Draft — hidden from the public site until you publish it"
                          data-testid={`draft-badge-${kidsClass.id}`}>
                          Draft — not live
                        </span>
                      )}
                      {isPaused && (
                        <span className="text-xs bg-amber-100 text-amber-800 border border-amber-300 px-2 py-1 rounded font-medium">Paused</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" aria-label="View attendees" title="View attendees" onClick={() => handleViewAttendees(kidsClass)}>
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-emerald-700" aria-label="Share roster with teacher"
                        title="Share roster with teacher" onClick={() => setShareRoster(kidsClass)}
                        data-testid={`share-roster-btn-${kidsClass.id}`}>
                        <Share2 size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-cafa-red" onClick={() => setEmailClass(kidsClass)} data-testid={`email-class-${kidsClass.id}`} aria-label="Email participants" title="Email Participants">
                        <Mail size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" aria-label="Copy to next term" title="Copy to next term"
                        disabled={isPrivate} onClick={() => setNextTerm({ singleProgram: kidsClass })} data-testid={`copy-term-btn-${kidsClass.id}`}>
                        <CalendarPlus size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" aria-label="Edit class" title="Edit class" onClick={() => handleEdit(kidsClass)}>
                        <Edit2 size={16} />
                      </Button>
                      {!isPrivate && <Button variant="ghost" size="sm" aria-label="Archive class" title="Archive class" onClick={() => setArchivingClass(kidsClass)} data-testid={`archive-class-${kidsClass.id}`}>
                        <Archive size={16} />
                      </Button>}
                    </div>
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-1">{kidsClass.title}</h3>
                  <p className="text-sm text-cafa-red mb-2">{kidsClass.instructor}</p>
                  <div className="text-xs text-stone-500 space-y-1">
                    {isPrivate ? (
                      <>
                        <p><Clock size={12} className="inline mr-1" />By appointment — one-on-one</p>
                        <p><DollarSign size={12} className="inline mr-1" />
                          {(kidsClass.private_session_options || []).map(o => `${o.label} $${o.price}`).join(' · ') || 'Session prices not set'}
                        </p>
                      </>
                    ) : (
                      <>
                        <p><Clock size={12} className="inline mr-1" />{kidsClass.schedule || kidsClass.daysOfWeek} • {kidsClass.time}</p>
                        <p><DollarSign size={12} className="inline mr-1" />${kidsClass.price} (Member: ${kidsClass.memberPrice || kidsClass.price})</p>
                      </>
                    )}
                    {!isPrivate && (kidsClass.startDate || isDraft) && (
                      <p><Calendar size={12} className="inline mr-1" />
                        {kidsClass.startDate
                          ? `${kidsClass.startDate}${kidsClass.endDate ? ` → ${kidsClass.endDate}` : ''}`
                          : <span className="text-amber-700 font-medium">Dates not set</span>}
                      </p>
                    )}
                  </div>
                  {isDraft && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-xs text-amber-900 mb-2">
                        {missingDates && !isPrivate
                          ? 'Set the start and end dates for this term, then publish to open registration.'
                          : 'Ready to go live? Publishing makes this class visible and bookable on the website.'}
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
                          disabled={publishing === kidsClass.id || (missingDates && !isPrivate) || (!kidsClass.endDate && !isPrivate)}
                          onClick={() => handlePublishClass(kidsClass)}
                          data-testid={`publish-btn-${kidsClass.id}`}>
                          <Rocket size={14} className="mr-1" />
                          {publishing === kidsClass.id ? 'Publishing…' : 'Publish'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(kidsClass)}
                          data-testid={`set-dates-btn-${kidsClass.id}`}>
                          <Calendar size={14} className="mr-1" /> Set Dates
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <span className="text-sm">
                      {isPrivate ? (
                        <><strong>{enrollmentCounts[String(kidsClass.id)] || 0}</strong> lesson(s) booked</>
                      ) : (
                        <><strong>{enrollmentCounts[String(kidsClass.id)] || 0}</strong> / {kidsClass.maxStudents || 12} enrolled</>
                      )}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isDraft || isPaused}
                      title={isDraft ? 'Publish this draft before adding students' : isPaused ? 'Reopen this class before adding students' : undefined}
                      onClick={() => { setShowAddStudentModal(kidsClass); setStudentFormData({ ...emptyStudent, amount_paid: kidsClass.price }); }}
                    >
                      <UserPlus size={14} className="mr-1" /> Add Student
                    </Button>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}

        </>}

        <ArchiveClassDialog program={archivingClass} token={token} onClose={() => setArchivingClass(null)} onArchived={fetchData} />

        {/* Edit Modal */}
        {editingClass && (
          <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-3xl w-full p-6 my-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-stone-900">Edit Kids Class</h2>
                <Button variant="ghost" size="sm" onClick={() => setEditingClass(null)}><X size={18} /></Button>
              </div>
              {classFormContent(true)}
              <div className="flex items-center gap-4 mt-6 pt-4 border-t border-stone-200">
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white">
                  <Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditingClass(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-3xl w-full p-6 my-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-stone-900">Add New Kids Class</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}><X size={18} /></Button>
              </div>
              {classFormContent(false)}
              <div className="flex items-center gap-4 mt-6 pt-4 border-t border-stone-200">
                <Button onClick={handleAdd} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white">
                  <Plus size={16} className="mr-1" /> {saving ? 'Creating...' : 'Create Kids Class'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* Attendees Modal */}
        {showAttendeesModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold">{showAttendeesModal.status === 'archived' ? 'Archived Roster' : 'Enrolled Students'} — {showAttendeesModal.title}</h2>
                  {showAttendeesModal.current_term_label && (
                    <p className="text-xs text-stone-500 mt-0.5">Current term: <strong>{showAttendeesModal.current_term_label}</strong></p>
                  )}
                </div>
                <Button variant="ghost" size="sm" aria-label="Close roster" onClick={() => setShowAttendeesModal(null)}><X size={18} /></Button>
              </div>

              {/* Term selector */}
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="roster-term-selector">
                <span className="text-xs font-medium text-stone-600 inline-flex items-center gap-1"><History size={12} /> Roster:</span>
                <button
                  onClick={() => { setRosterTerm('current'); fetchAttendees(showAttendeesModal.id, 'current'); }}
                  className={`text-xs px-2 py-1 rounded border ${rosterTerm === 'current' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                >Current term</button>
                {termHistory.length > 0 && termHistory.map(t => (
                  <button
                    key={t.term_label}
                    onClick={() => { setRosterTerm(t.term_label); fetchAttendees(showAttendeesModal.id, t.term_label); }}
                    className={`text-xs px-2 py-1 rounded border ${rosterTerm === t.term_label ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                    title={t.archived_at ? `Archived ${new Date(t.archived_at).toLocaleDateString()}` : ''}
                  >
                    {t.term_label} ({t.count})
                  </button>
                ))}
                <button
                  onClick={() => { setRosterTerm('all'); fetchAttendees(showAttendeesModal.id, 'all'); }}
                  className={`text-xs px-2 py-1 rounded border ${rosterTerm === 'all' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                >All</button>
              </div>

              {attendees.length === 0 ? (
                <p className="text-stone-500 text-center py-8">
                  {rosterTerm === 'current' ? 'No students enrolled in the current term' : 'No students for this term'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Student</th>
                        <th className="text-left py-2">Email</th>
                        <th className="text-left py-2">Phone</th>
                        <th className="text-left py-2">Paid</th>
                        <th className="text-left py-2">Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendees.map((student) => {
                        const isRefunded = ['refunded', 'manual', 'cancelled'].includes(student.refund_status);
                        const isArchived = !!student.archived_term || showAttendeesModal.status === 'archived';
                        return (
                          <tr key={student.id} className={`border-b ${isRefunded ? 'opacity-60' : ''}`}>
                            <td className={`py-2 ${isRefunded ? 'line-through text-stone-400' : ''}`}>{student.student_name}</td>
                            <td className={`py-2 ${isRefunded ? 'line-through text-stone-400' : ''}`}>{student.student_email}</td>
                            <td className="py-2" data-testid={`kids-attendee-phone-${student.id}`}>
                              {student.phone ? (
                                <a href={`tel:${student.phone}`} className="text-amber-700 hover:underline">{student.phone}</a>
                              ) : '—'}
                            </td>
                            <td className="py-2">${student.amount_paid || 0}</td>
                            <td className="py-2">
                              {isRefunded ? (
                                <span
                                  className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800 border border-amber-300"
                                  title={`${student.refund_status}${student.refund_reason ? ' — ' + student.refund_reason : ''}${student.refunded_at ? ' on ' + new Date(student.refunded_at).toLocaleDateString() : ''}`}
                                  data-testid={`refund-badge-${student.id}`}
                                >Refunded</span>
                              ) : isArchived ? (
                                <span className="px-2 py-0.5 text-xs rounded bg-stone-100 text-stone-700 border border-stone-300">Past term</span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800 border border-emerald-300">Enrolled</span>
                              )}
                            </td>
                            <td className="py-2">
                              {!isRefunded && !isArchived && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-700 hover:bg-amber-50"
                                  onClick={() => openRefundModal(showAttendeesModal.id, student)}
                                  title="Refund / remove from roster"
                                  data-testid={`refund-btn-${student.id}`}
                                >
                                  <RotateCcw size={14} className="mr-1" /> Refund
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-4 pt-4 border-t flex items-center gap-2 flex-wrap">
                {isProgramPublic(showAttendeesModal) && <Button onClick={() => { setShowAddStudentModal(showAttendeesModal); setStudentFormData({ ...emptyStudent, amount_paid: showAttendeesModal.price }); }}>
                  <UserPlus size={16} className="mr-2" /> Add Student
                </Button>}
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  onClick={() => { const program = showAttendeesModal; setShowAttendeesModal(null); setNextTerm({ singleProgram: program }); }}
                  disabled={isPrivateLesson(showAttendeesModal)}
                  data-testid="create-next-term-from-roster"
                >
                  <CalendarPlus size={16} className="mr-2" /> Create Next Term
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Refund Modal */}
        {refundModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="refund-modal">
            <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <RotateCcw size={20} className="text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-stone-900">Refund this student?</h3>
                  <p className="text-sm text-stone-600">
                    <span className="font-medium">{refundModal.student.student_name}</span> — ${refundModal.student.amount_paid || 0} paid
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <Label className="text-sm font-medium">Refund mode</Label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50 has-[:checked]:bg-amber-50 has-[:checked]:border-amber-300">
                    <input
                      type="radio" name="refund-mode" value="paypal_auto"
                      checked={refundForm.mode === 'paypal_auto'}
                      onChange={() => setRefundForm({ ...refundForm, mode: 'paypal_auto' })}
                      disabled={!refundModal.student.paypal_capture_id && !refundModal.student.payment_id}
                      data-testid="refund-mode-paypal"
                    />
                    <div className="text-sm">
                      <div className="font-medium">Auto-refund via PayPal</div>
                      <div className="text-xs text-stone-500">
                        Calls PayPal to issue the refund automatically.
                        {!refundModal.student.paypal_capture_id && !refundModal.student.payment_id && ' (unavailable — no PayPal capture on this registration)'}
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50 has-[:checked]:bg-amber-50 has-[:checked]:border-amber-300">
                    <input type="radio" name="refund-mode" value="manual"
                      checked={refundForm.mode === 'manual'}
                      onChange={() => setRefundForm({ ...refundForm, mode: 'manual' })}
                      data-testid="refund-mode-manual"
                    />
                    <div className="text-sm">
                      <div className="font-medium">Mark refunded (offline)</div>
                      <div className="text-xs text-stone-500">For cash, check, or already-refunded payments. Records the refund without calling PayPal.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50 has-[:checked]:bg-amber-50 has-[:checked]:border-amber-300">
                    <input type="radio" name="refund-mode" value="cancel_only"
                      checked={refundForm.mode === 'cancel_only'}
                      onChange={() => setRefundForm({ ...refundForm, mode: 'cancel_only' })}
                      data-testid="refund-mode-cancel"
                    />
                    <div className="text-sm">
                      <div className="font-medium">Cancel only (no refund)</div>
                      <div className="text-xs text-stone-500">Free registration or duplicate sign-up where no money changed hands.</div>
                    </div>
                  </label>
                </div>

                <div>
                  <Label htmlFor="refund-reason" className="text-sm">Reason (optional, visible in audit log)</Label>
                  <Input
                    id="refund-reason"
                    value={refundForm.reason}
                    onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                    placeholder="e.g. duplicate registration, pre-class dropout"
                    className="mt-1"
                    data-testid="refund-reason-input"
                  />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox"
                    checked={refundForm.send_email}
                    onChange={(e) => setRefundForm({ ...refundForm, send_email: e.target.checked })}
                    data-testid="refund-send-email-toggle"
                  />
                  Send refund confirmation email to {refundModal.student.student_email || 'student'}
                </label>
              </div>

              <p className="text-xs text-stone-500 mb-4 flex items-start gap-1">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                Non-destructive — the registration record stays in the system for audit. The student no longer counts toward enrollment capacity.
              </p>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRefundModal(null)} disabled={refundSubmitting} data-testid="refund-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleRefundSubmit}
                  disabled={refundSubmitting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="refund-confirm-btn"
                >
                  {refundSubmitting ? 'Processing…' : 'Confirm Refund'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Student Modal */}
        {showAddStudentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Enroll Student - {showAddStudentModal.title}</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowAddStudentModal(null)}><X size={18} /></Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Student Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={studentFormData.student_name}
                    onChange={(e) => setStudentFormData(prev => ({ ...prev, student_name: e.target.value }))}
                    className="mt-1"
                    placeholder="Child&apos;s full name"
                  />
                </div>
                <div>
                  <Label>Parent/Guardian Name</Label>
                  <Input
                    value={studentFormData.parent_name}
                    onChange={(e) => setStudentFormData(prev => ({ ...prev, parent_name: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Parent Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={studentFormData.parent_email}
                    onChange={(e) => setStudentFormData(prev => ({ ...prev, parent_email: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Parent Phone</Label>
                  <Input
                    value={studentFormData.parent_phone}
                    onChange={(e) => setStudentFormData(prev => ({ ...prev, parent_phone: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Amount Paid ($)</Label>
                    <Input
                      type="number"
                      value={studentFormData.amount_paid}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, amount_paid: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Payment Method</Label>
                    <select
                      value={studentFormData.payment_method}
                      onChange={(e) => setStudentFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
                    >
                      <option value="paypal">PayPal</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="card">Card</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={studentFormData.notes}
                    onChange={(e) => setStudentFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="mt-1"
                    rows={2}
                    placeholder="Any special notes (allergies, pickup info, etc.)"
                  />
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <Button onClick={handleAddStudent} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900">
                  {saving ? 'Enrolling...' : 'Enroll Student'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddStudentModal(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}
      {/* Email Participants Modal */}
      <EmailParticipantsModal
        open={!!emailClass}
        onClose={() => setEmailClass(null)}
        sourceType="class"
        sourceId={emailClass ? String(emailClass.id) : ''}
        sourceName={emailClass?.title || ''}
        instructor={emailClass?.instructor || ''}
        schedule={emailClass?.schedule || ''}
      />

      {/* Copy to Next Term Modal */}
      <NextTermModal
        open={!!nextTerm}
        onClose={() => setNextTerm(null)}
        token={token}
        programs={[...kidsClasses, ...archivedClasses].filter(c => (c.status || 'active') !== 'draft' && !isPrivateLesson(c))}
        singleProgram={nextTerm?.singleProgram || null}
        onDone={() => { setArchiveView(false); fetchData(); }}
      />

      {shareRoster && (
        <ShareRosterModal
          itemType="program"
          item={shareRoster}
          token={token}
          onClose={() => setShareRoster(null)}
        />
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminKidsClasses;
